import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment } from './index.js';
import { a as toDateRangeString, b as toDateRange, e as exceedsMaxDays, t as toDateString, i as isRangeInOrder, d as defineCustomElement$7 } from './p-DA_Ioy3T.js';
import { f as forwardEvent } from './p-rUtJRH95.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, b as getError, w as watchValue, d as watchError, e as watchWarning, f as renderFormField } from './p-Cg8AdZPK.js';
import { g as getBoxAttributes } from './p-CXN6CHBw.js';
import { d as dateFnsExports } from './p-iWCgRMLC.js';
import { d as defineCustomElement$a } from './p-BbTg3Car.js';
import { d as defineCustomElement$9 } from './p-z4UJ58j1.js';
import { d as defineCustomElement$8 } from './p-BVYlUJum.js';
import { d as defineCustomElement$6 } from './p-Dpb0Xahb.js';
import { d as defineCustomElement$5 } from './p-Da2lAZSv.js';
import { d as defineCustomElement$4 } from './p-BtEI0Sh2.js';
import { d as defineCustomElement$3 } from './p-CT2oAZeh.js';
import { d as defineCustomElement$2 } from './p-CFAyJ2yW.js';
import { d as defineCustomElement$1 } from './p-ClSUnmWE.js';

function stringifyValue(dateRange) {
    return toDateRangeString(dateRange);
}
function parseValue(dateRangeString) {
    return toDateRange(dateRangeString);
}
const getNewValue = (idx, newDate, currentValue, maxDays) => {
    const newValue = parseValue(currentValue) ?? [null, null];
    // Update given idx with new date
    newValue[idx] = newDate || null;
    const [start, end] = newValue;
    if (start && end) {
        // When start is after end
        if (idx === 0 && dateFnsExports.isAfter(start, end)) {
            // Align end to start
            newValue[1] = start;
        }
        // When end is before start
        if (idx === 1 && dateFnsExports.isBefore(end, start)) {
            // Align start to end
            newValue[0] = end;
        }
    }
    if (maxDays && exceedsMaxDays(newValue, maxDays)) {
        const [start, end] = newValue;
        // limit end date
        if (idx === 0 && start) {
            newValue[1] = toDateString(dateFnsExports.addDays(start, maxDays - 1));
        }
        // limit start date
        if (idx === 1 && end) {
            newValue[0] = toDateString(dateFnsExports.subDays(end, maxDays - 1));
        }
    }
    return newValue;
};
function checkDateBounds({ date, pos, minDate, maxDate, }) {
    // Nothing to check if date is undefined
    if (!date) {
        return;
    }
    // If we can compare to minDate
    if (minDate && dateFnsExports.isBefore(date, minDate)) {
        throw new Error(`The ${pos} date may not be before the min date (${minDate}).`);
    }
    // If we can compare to maxDate
    if (maxDate && dateFnsExports.isAfter(date, maxDate)) {
        throw new Error(`The ${pos} date may not be after the max date. (${maxDate})`);
    }
}

const dateRangeInputCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.box{width:100%}.to-label{display:block;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:600;height:2rem;letter-spacing:-.0004em;line-height:1.25rem;line-height:2rem}";

const WuiDateRangeInput = /*@__PURE__*/ proxyCustomElement(class WuiDateRangeInput extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validateInternal = () => {
            // Read-only does not need validation
            if (this.readOnly) {
                return;
            }
            const dateRange = this.value ? parseValue(this.value) : undefined;
            if (this.required && (typeof dateRange === 'undefined' || !(dateRange?.[0] && dateRange[1]))) {
                return {
                    validationMessage: 'Please fill in this field.',
                    validity: getValidityObject({ customError: true, valueMissing: true, valid: false }),
                };
            }
            if (!isRangeInOrder(dateRange)) {
                return {
                    validationMessage: 'The end date may not be before the start date.',
                    validity: getValidityObject({ customError: true, valid: false }),
                };
            }
            try {
                checkDateBounds({ date: dateRange?.[0], pos: 'start', minDate: this.minDate, maxDate: this.maxDate });
            }
            catch (err) {
                if (err instanceof Error) {
                    return {
                        validationMessage: err.message,
                        validity: getValidityObject({ customError: true, valid: false }),
                    };
                }
            }
            try {
                checkDateBounds({ date: dateRange?.[1], pos: 'end', minDate: this.minDate, maxDate: this.maxDate });
            }
            catch (err) {
                if (err instanceof Error) {
                    return {
                        validationMessage: err.message,
                        validity: getValidityObject({ customError: true, valid: false }),
                    };
                }
            }
            if (exceedsMaxDays(dateRange, this.maxDays)) {
                return {
                    validationMessage: `Date range may not exceed ${this.maxDays} days.`,
                    validity: getValidityObject({ customError: true, valid: false }),
                };
            }
            return {
                validationMessage: '',
                validity: getValidityObject(),
            };
        };
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.dirty = false;
        this.getError = getError(this);
        //////// FORM FIELD END ////////
        this.name = '';
        this.disabled = false;
        /**
         * Disable the component and only use it to display values.
         *
         * This state can be used to hide the calendar, skip validation and disable the inputs.
         */
        this.readOnly = false;
        this.placeholder = 'Select a date range';
        this.handleValueChange = (idx, date) => {
            if (typeof date === 'undefined' || !date) {
                return;
            }
            const newValue = getNewValue(idx, date, this.value, this.maxDays);
            if (typeof newValue === 'undefined') {
                return;
            }
            this.setValue(newValue);
        };
        this.refs = {
            start: null,
            end: null,
        };
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    setValue(value) {
        const newValue = value ? stringifyValue(value) : undefined;
        if (this.value === newValue) {
            // Don't emit same value
            return;
        }
        this.value = newValue;
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        this.dirty = true;
        this.checkValidity();
    }
    renderInner() {
        let valueParsed;
        try {
            valueParsed = parseValue(this.value);
        }
        catch {
            // ignore bad values
        }
        return (h(Fragment, null, h("wui-box", { orientation: "horizontal", gutter: "sm", align: "end", class: "box", ...getBoxAttributes(this) }, h("wui-date-input", { ref: (ref) => {
                this.refs.start = ref ?? null;
            }, value: valueParsed?.[0] ?? undefined, rangeEndDate: valueParsed?.[1] ?? undefined, onClearRangeEndDate: (e) => {
                e.stopPropagation();
            }, onInput: (e) => {
                const target = e.currentTarget;
                if (!target) {
                    return;
                }
                this.handleValueChange(0, target.value);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            }, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, error: this.showValidity ? !!this.validationMessage : false, disabled: this.disabled || this.readOnly, min: this.minDate, max: this.maxDate, maxDays: this.maxDays, required: this.required, formField: { label: 'Start date', ...this.dateInputStart?.formField }, initialCalendarDisplayDate: this.initialCalendarDisplayDate }), h("b", { class: "to-label" }, "to"), h("wui-date-input", { ref: (ref) => {
                this.refs.end = ref ?? null;
            }, value: valueParsed?.[1] ?? undefined, rangeStartDate: valueParsed?.[0] ?? undefined, onClearRangeStartDate: (e) => {
                e.stopPropagation();
            }, onInput: (e) => {
                const target = e.currentTarget;
                if (!target) {
                    return;
                }
                this.handleValueChange(1, target.value);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            }, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, error: this.showValidity ? !!this.validationMessage : false, disabled: this.disabled || this.readOnly, min: this.minDate, max: this.maxDate, maxDays: this.maxDays, required: this.required, formField: { label: 'End date', ...this.dateInputEnd?.formField }, initialCalendarDisplayDate: this.initialCalendarDisplayDate }))));
    }
    render() {
        return renderFormField(this, this.renderInner());
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return dateRangeInputCss; }
}, [257, "wui-date-range-input", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "name": [1],
        "disabled": [516],
        "readOnly": [516, "read-only"],
        "placeholder": [1],
        "minDate": [1, "min-date"],
        "maxDate": [1, "max-date"],
        "initialCalendarDisplayDate": [1, "initial-calendar-display-date"],
        "maxDays": [2, "max-days"],
        "dateInputStart": [16, "date-input-start"],
        "dateInputEnd": [16, "date-input-end"],
        "valueAsDateRange": [1040, "value-as-date-range"],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
WuiDateRangeInput.stringifyValue = stringifyValue;
WuiDateRangeInput.parseValue = parseValue;
Object.defineProperties(WuiDateRangeInput.prototype, {
    valueAsDateRange: {
        configurable: true,
        get() {
            return parseValue(this.value);
        },
        set(newValue) {
            this.value = newValue ? stringifyValue(newValue) : undefined;
            this.dirty = true;
            this.checkValidity();
        },
    },
});
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-range-input", "wui-box", "wui-button", "wui-date-input", "wui-date-input-calendar", "wui-form-field", "wui-icon", "wui-loader", "wui-menu-pane", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-range-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateRangeInput);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-date-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiDateRangeInput as W, defineCustomElement as d, parseValue as p, stringifyValue as s };
